import { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Banknote, Calendar, TrendingDown, TrendingUp, Wallet } from 'lucide-react';
import {
  ContextHero,
  DataCard,
  EmptyStateModern,
  KpiCard,
  MoneyAmount,
  PageContainer,
  SectionTitle,
  SkeletonShimmer,
} from '@/components/design-system';
import { useEcheancierParActivite } from '@/hooks/queries/useCaisseGroupe';
import { EvolutionSoldeChart } from '@/components/charts/EvolutionSoldeChart';
import { cn } from '@/lib/utils';

export function EcheanciersTab(): JSX.Element {
  const { data: rows = [], isLoading } = useEcheancierParActivite();
  const [selected, setSelected] = useState<string>('groupe');

  const groupe = rows.reduce(
    (acc, r) => ({
      a_encaisser_30j: acc.a_encaisser_30j + Number(r.a_encaisser_30j),
      a_payer_30j: acc.a_payer_30j + Number(r.a_payer_30j),
      charges_recurrentes_30j: acc.charges_recurrentes_30j + Number(r.charges_recurrentes_30j),
      avances_a_recevoir: acc.avances_a_recevoir + Number(r.avances_a_recevoir),
      avances_a_rembourser: acc.avances_a_rembourser + Number(r.avances_a_rembourser),
      solde_courant: acc.solde_courant + Number(r.solde_courant),
    }),
    {
      a_encaisser_30j: 0,
      a_payer_30j: 0,
      charges_recurrentes_30j: 0,
      avances_a_recevoir: 0,
      avances_a_rembourser: 0,
      solde_courant: 0,
    },
  );

  const data = selected === 'groupe' ? groupe : rows.find((r) => r.activite_id === selected);
  const solde =
    (data?.solde_courant ?? 0) +
    (data?.a_encaisser_30j ?? 0) -
    (data?.a_payer_30j ?? 0) -
    (data?.charges_recurrentes_30j ?? 0) +
    (data?.avances_a_recevoir ?? 0) -
    (data?.avances_a_rembourser ?? 0);

  return (
    <PageContainer>
      <div className="space-y-6">
        <ContextHero
          variant="finances"
          icon={<Calendar className="h-5 w-5" />}
          title="Échéanciers par activité"
          subtitle="Solde prévisionnel 30j par caisse — visualise à encaisser, à payer, charges récurrentes et avances inter-activités."
        />

        {/* Sélecteur activité */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <ActiviteSelector
            label="📊 Vue Groupe consolidée"
            slug="groupe"
            selected={selected === 'groupe'}
            onClick={() => setSelected('groupe')}
          />
          {rows.map((r) => (
            <ActiviteSelector
              key={r.activite_id}
              label={r.activite_nom}
              slug={r.activite_id}
              selected={selected === r.activite_id}
              onClick={() => setSelected(r.activite_id)}
            />
          ))}
        </div>

        {isLoading ? (
          <SkeletonShimmer variant="card" count={3} />
        ) : !data ? (
          <EmptyStateModern
            variant="finances"
            icon={<Calendar className="h-7 w-7" />}
            title="Aucune donnée échéancier"
            description="Sélectionnez une activité ou créez des échéances pour voir le prévisionnel apparaître."
          />
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <KpiCard
                variant="finances"
                label="Solde caisse"
                value={<MoneyAmount amount={data.solde_courant} variant="inline" withColor />}
                icon={<Wallet className="h-4 w-4" />}
              />
              <KpiCard
                variant="echeances"
                label="À encaisser 30j"
                value={<MoneyAmount amount={data.a_encaisser_30j} variant="inline" />}
                icon={<TrendingUp className="h-4 w-4" />}
              />
              <KpiCard
                variant="finances"
                label="À payer 30j"
                value={<MoneyAmount amount={data.a_payer_30j} variant="inline" />}
                icon={<TrendingDown className="h-4 w-4" />}
              />
              <KpiCard
                variant="finances"
                label="Charges récur. 30j"
                value={<MoneyAmount amount={data.charges_recurrentes_30j} variant="inline" />}
                icon={<Banknote className="h-4 w-4" />}
              />
              <KpiCard
                variant="echeances"
                label="Avances à recevoir"
                value={<MoneyAmount amount={data.avances_a_recevoir} variant="inline" />}
              />
              <KpiCard
                variant="finances"
                label="Avances à rembourser"
                value={<MoneyAmount amount={data.avances_a_rembourser} variant="inline" />}
              />
            </div>

            <DataCard
              title="Solde prévisionnel fin de mois"
              subtitle={`${selected === 'groupe' ? 'Toutes activités' : data && 'activite_nom' in data ? data.activite_nom : ''}`}
            >
              <div
                className={cn(
                  'rounded-2xl p-6 text-center',
                  solde >= 0
                    ? 'bg-gradient-to-br from-emerald-50 to-teal-100'
                    : 'bg-gradient-to-br from-rose-50 to-pink-100',
                )}
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  Projection 30j
                </div>
                <motion.div
                  key={solde}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-1"
                >
                  <MoneyAmount amount={solde} variant="kpi" withColor />
                </motion.div>
                <div className="mt-2 text-xs text-slate-600">
                  Solde courant + à encaisser − à payer − charges récurrentes ± avances
                </div>
              </div>
            </DataCard>

            <EvolutionSoldeChart />

            {selected === 'groupe' && (
              <>
                <SectionTitle>Top risques cash-flow 30j</SectionTitle>
                <DataCard>
                  {groupe.charges_recurrentes_30j === 0 && groupe.a_payer_30j === 0 ? (
                    <div className="py-3 text-center text-sm text-slate-500">
                      Aucune sortie majeure prévue dans les 30 prochains jours.
                    </div>
                  ) : (
                    <ul className="space-y-2 text-sm">
                      {groupe.charges_recurrentes_30j > 0 && (
                        <li className="flex items-center justify-between gap-2 rounded-lg bg-rose-50 p-2 ring-1 ring-rose-200">
                          <span className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-rose-600" />
                            Charges récurrentes (loyer + salaires + utilities)
                          </span>
                          <MoneyAmount
                            amount={-groupe.charges_recurrentes_30j}
                            variant="inline"
                            withColor
                          />
                        </li>
                      )}
                      {groupe.a_payer_30j > 0 && (
                        <li className="flex items-center justify-between gap-2 rounded-lg bg-orange-50 p-2 ring-1 ring-orange-200">
                          <span className="flex items-center gap-2">
                            <TrendingDown className="h-4 w-4 text-orange-600" />
                            Échéances fournisseurs ouvertes 30j
                          </span>
                          <MoneyAmount amount={-groupe.a_payer_30j} variant="inline" withColor />
                        </li>
                      )}
                      {groupe.avances_a_rembourser > 0 && (
                        <li className="flex items-center justify-between gap-2 rounded-lg bg-amber-50 p-2 ring-1 ring-amber-200">
                          <span className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                            Avances inter-activités à rembourser
                          </span>
                          <MoneyAmount
                            amount={-groupe.avances_a_rembourser}
                            variant="inline"
                            withColor
                          />
                        </li>
                      )}
                    </ul>
                  )}
                </DataCard>
              </>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
}

function ActiviteSelector({
  label,
  slug,
  selected,
  onClick,
}: {
  label: string;
  slug: string;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-2xl border-2 p-4 text-left transition-all',
        selected
          ? 'border-orange-500 bg-orange-50 shadow-md'
          : 'border-slate-200 bg-white hover:border-slate-300',
      )}
    >
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
        Échéancier
      </div>
      <div className="mt-1 text-sm font-bold text-slate-800">{label}</div>
      <span className="hidden">{slug}</span>
    </button>
  );
}
